import { apiClient } from './apiClient';
import { toast } from 'sonner';

/**
 * Service for profile-related operations
 * Handles profile creation, updates, and management
 */
export const profileService = {
  /**
   * Ensure a user profile exists by calling the ensure-profile edge function
   * This is typically used for newly invited users or users without profiles
   */
  ensureProfile: async (): Promise<{ success: boolean; error?: string; data?: any }> => {
    try {
      console.log("🔄 Ensuring profile exists...");
      
      // Use the Supabase client method (preferred) for better reliability
      const { data, error } = await apiClient.invokeEdgeFunction('ensure-profile');
      
      if (error) {
        console.error("❌ Profile creation failed:", error);
        
        // Handle specific error cases with better user feedback
        if (error.message?.includes('Profile already exists')) {
          console.log("✅ Profile already exists");
          return { success: true, data: { message: 'Profile already exists' } };
        } else if (error.message?.includes('Invalid or expired token')) {
          return { 
            success: false, 
            error: 'Your session has expired. Please log in again.' 
          };
        } else if (error.message?.includes('User not found')) {
          return { 
            success: false, 
            error: 'User not found. Please contact support.' 
          };
        } else if (error.message?.includes('Server configuration error')) {
          return { 
            success: false, 
            error: 'Server configuration issue. Please contact support.' 
          };
        } else {
          return { 
            success: false, 
            error: `Profile setup failed: ${error.message || 'Unknown error'}` 
          };
        }
      }

      if (data?.error) {
        console.error("❌ Profile creation returned error:", data.error);
        return { 
          success: false, 
          error: `Profile setup failed: ${data.error}` 
        };
      }
      
      console.log("✅ Profile ensured successfully");
      return { 
        success: true, 
        data: data || { message: 'Profile created successfully' } 
      };
      
    } catch (error) {
      console.error("❌ Error ensuring profile:", error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      return { 
        success: false, 
        error: `Profile setup failed: ${errorMessage}` 
      };
    }
  },

  /**
   * Ensure profile with fallback to direct fetch method
   * This is used when the Supabase client method fails
   */
  ensureProfileWithFallback: async (): Promise<{ success: boolean; error?: string; data?: any }> => {
    try {
      console.log("🔄 Ensuring profile exists (with fallback)...");
      
      // First try the Supabase client method
      const result = await profileService.ensureProfile();
      if (result.success) {
        return result;
      }
      
      // If that fails, try the direct fetch method
      console.log("⚠️ Supabase client method failed, trying direct fetch...");
      const { data, error } = await apiClient.callEdgeFunction('ensure-profile');
      
      if (error) {
        console.error("❌ Direct fetch also failed:", error);
        return { 
          success: false, 
          error: `Profile setup failed: ${error.message || 'Unknown error'}` 
        };
      }

      if (data?.error) {
        console.error("❌ Direct fetch returned error:", data.error);
        return { 
          success: false, 
          error: `Profile setup failed: ${data.error}` 
        };
      }
      
      console.log("✅ Profile ensured successfully via direct fetch");
      return { 
        success: true, 
        data: data || { message: 'Profile created successfully' } 
      };
      
    } catch (error) {
      console.error("❌ Error ensuring profile with fallback:", error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      return { 
        success: false, 
        error: `Profile setup failed: ${errorMessage}` 
      };
    }
  },

  /**
   * Get user profile by ID using RPC function
   * This is used to fetch profile data after creation
   */
  getProfileById: async (userId: string): Promise<{ success: boolean; data?: any; error?: string }> => {
    try {
      const { supabase } = await import('@/integrations/supabase/client');
      
      // @ts-expect-error - Supabase type inference issue
      const { data, error } = await supabase.rpc('get_profile_by_id', {
        user_id: userId
      });
      
      if (error) {
        console.error("❌ Error fetching profile:", error);
        return { 
          success: false, 
          error: `Failed to fetch profile: ${error.message}` 
        };
      }
      
      // Type guard to ensure data is an array
      if (!data) {
        return { 
          success: false, 
          error: 'Profile not found' 
        };
      }
      
      // Type assertion to help TypeScript understand the data structure
      const dataArray = data as unknown[];
      if (!Array.isArray(dataArray) || dataArray.length === 0) {
        return { 
          success: false, 
          error: 'Profile not found' 
        };
      }
      
      return { 
        success: true, 
        data: dataArray[0] 
      };
      
    } catch (error) {
      console.error("❌ Error fetching profile:", error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      return { 
        success: false, 
        error: `Failed to fetch profile: ${errorMessage}` 
      };
    }
  }
};
